/**
 * Films Ground's chronological-order guard — the one integrity check Phase B exists to enforce
 * (see `films-ground-anchor-review-mockup.html`'s `checkOrder()`, the behavioral source for this
 * port). Anchors are laid out in fixed RECORDING order (`anchorId`, 1..N, assigned by n8n and
 * never reassigned here); each anchor's user-resolved gamebook UID must have a strictly higher
 * play number than its nearest RESOLVED predecessor and a strictly lower one than its nearest
 * RESOLVED successor — an unresolved anchor in between is simply skipped, so its neighbors are
 * compared directly across the gap.
 *
 * Pure and framework-free by design: the mockup's version reads/writes the DOM directly, but the
 * algorithm itself has nothing to do with rendering. Kept this way so it's independently
 * unit-testable and so a future backend port (server-side re-validation before accepting a Phase B
 * submit) can mirror the exact same logic rather than re-deriving it from scratch.
 */

export interface OrderCheckEntry {
  /** Recording-order rank. Real anchors use their `anchorId` (1..N); the confirmed game-start
   *  boundary from Phase A is rank 0 — it always precedes every anchor and is always "resolved". */
  anchorId: number;
  /** The resolved gamebook UID for this rank, or `null` if not yet resolved (excluded from the
   *  comparison chain — its neighbors compare against each other instead). */
  uid: string | null;
}

/** Pulls the numeric play number out of a UID like `"P031"` -> `31`. `null` for anything that
 *  doesn't contain a number (defensive — every real UID does). */
function uidNum(uid: string | null): number | null {
  if (!uid) {
    return null;
  }
  const match = uid.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : null;
}

/**
 * Returns the set of `anchorId`s whose resolved UID is NOT strictly between its nearest resolved
 * neighbors' UIDs in recording order. Unresolved anchors (`uid: null`) are never themselves
 * flagged — there's nothing to check yet — but still participate in the chain
 * (`entries` need not be pre-sorted by `anchorId`; this sorts internally, mirroring the mockup's
 * own defensive re-sort).
 */
export function checkOrder(entries: OrderCheckEntry[]): Set<number> {
  const resolved = entries
    .map((e) => ({ anchorId: e.anchorId, n: uidNum(e.uid) }))
    .filter((e): e is { anchorId: number; n: number } => e.n !== null)
    .sort((a, b) => a.anchorId - b.anchorId);

  const outOfOrder = new Set<number>();
  for (let i = 0; i < resolved.length; i++) {
    const prev = resolved[i - 1];
    const next = resolved[i + 1];
    const current = resolved[i];
    if ((prev && current.n <= prev.n) || (next && current.n >= next.n)) {
      outOfOrder.add(current.anchorId);
    }
  }
  return outOfOrder;
}
